import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../contexts/CartContext";
import { useI18n } from "../contexts/I18nContext";
import OrderItemCard from "../components/OrderItemCard";
import VolumeDiscountRow from "../components/VolumeDiscountRow";
import { summarizeConfiguration } from "../lib/orderSummary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const { items, clear } = useCart();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    city: "",
    postal_code: "",
    country: "France",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // URL Stripe gardée en mémoire pour fallback "ouvrir dans un nouvel onglet"
  // si la redirection automatique est bloquée (cookies tiers, extension).
  const [fallbackUrl, setFallbackUrl] = useState(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error(t.cart.empty);
      return;
    }
    setSubmitting(true);
    try {
      // Construction des metadata détaillées : un résumé concis par item +
      // une chaîne globale pour le suivi Stripe.
      const itemsSummary = items.map((it, idx) => {
        const slug = it.product_slug;
        const summary = summarizeConfiguration(slug, it.configuration || {}, 200);
        const unit = Math.round(Number(it.unit_price_chf) || 0);
        return `[${idx + 1}] ${slug} ×${it.quantity || 1} · ${summary} · ${unit} €/u`;
      }).join(" | ");

      const metadata = {
        customer_name: form.name,
        customer_phone: form.phone || "",
        customer_company: form.company || "",
        shipping_address: `${form.address}, ${form.postal_code} ${form.city}, ${form.country}`.trim(),
        notes: (form.notes || "").slice(0, 400),
        items_summary: itemsSummary.slice(0, 480),
        items_count: String(items.length),
      };

      const resp = await axios.post(`${API}/checkout/session`, {
        items: items.map((i) => ({
          product_slug: i.product_slug,
          product_name: i.product_name?.[lang] || i.product_name?.fr || i.product_name,
          configuration: i.configuration || {},
          quantity: i.quantity || 1,
        })),
        origin_url: window.location.origin,
        locale: lang,
        customer_email: form.email,
        metadata,
      });
      if (resp.data?.url) {
        // Garde l'URL en mémoire pour permettre un fallback "ouvrir dans
        // un nouvel onglet" si la redirection est bloquée par le navigateur.
        setFallbackUrl(resp.data.url);
        // Redirection vers Stripe Checkout (page hébergée)
        window.location.assign(resp.data.url);
        // Si après 3s on est toujours là, c'est que la redirection a été
        // bloquée — on laisse alors le bouton de secours visible.
        setTimeout(() => {
          if (document.visibilityState === "visible") {
            setSubmitting(false);
          }
        }, 3000);
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || t.checkout.error;
      toast.error(typeof msg === "string" ? msg : t.checkout.error);
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#0a0a0a] border border-white/10 px-4 py-3 font-body text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14] focus:shadow-[0_0_18px_rgba(57,255,20,0.2)] transition-all";
  const labelCls =
    "font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 block";

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="checkout-success">
        <CheckCircle2 size={72} className="text-[#39FF14] mx-auto mb-8 drop-shadow-[0_0_25px_rgba(57,255,20,0.5)]" />
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">
          <span className="chrome-text">{t.checkout.success}</span>
        </h1>
        <p className="mt-5 text-zinc-400 font-body">{t.checkout.subtitle}</p>
        <button
          onClick={() => navigate("/")}
          className="btn-neon mt-10 inline-flex"
          data-testid="checkout-back-home"
        >
          {t.nav.home}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen">
      <section className="relative py-20 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="label-tech">{t.checkout.title}</span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-bold uppercase tracking-tight">
            <span className="chrome-text">{t.checkout.title}</span>
          </h1>
          <p className="mt-5 text-zinc-400 font-body max-w-2xl">{t.checkout.subtitle}</p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[1fr_380px] gap-10">
        <form onSubmit={onSubmit} className="space-y-5" data-testid="checkout-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t.checkout.name}</label>
              <input required name="name" value={form.name} onChange={onChange} className={inputCls} data-testid="checkout-name-input" />
            </div>
            <div>
              <label className={labelCls}>{t.checkout.email}</label>
              <input required type="email" name="email" value={form.email} onChange={onChange} className={inputCls} data-testid="checkout-email-input" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t.checkout.phone}</label>
              <input name="phone" value={form.phone} onChange={onChange} className={inputCls} data-testid="checkout-phone-input" />
            </div>
            <div>
              <label className={labelCls}>{t.checkout.company}</label>
              <input name="company" value={form.company} onChange={onChange} className={inputCls} data-testid="checkout-company-input" />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t.checkout.address}</label>
            <input name="address" value={form.address} onChange={onChange} className={inputCls} data-testid="checkout-address-input" />
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <div>
              <label className={labelCls}>{t.checkout.postal_code}</label>
              <input name="postal_code" value={form.postal_code} onChange={onChange} className={inputCls} data-testid="checkout-postal-input" />
            </div>
            <div>
              <label className={labelCls}>{t.checkout.city}</label>
              <input name="city" value={form.city} onChange={onChange} className={inputCls} data-testid="checkout-city-input" />
            </div>
            <div>
              <label className={labelCls}>{t.checkout.country}</label>
              <input name="country" value={form.country} onChange={onChange} className={inputCls} data-testid="checkout-country-input" />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t.checkout.notes}</label>
            <textarea name="notes" value={form.notes} onChange={onChange} rows={5} className={inputCls} data-testid="checkout-notes-input" />
          </div>
          <button type="submit" disabled={submitting || items.length === 0} className="btn-neon disabled:opacity-50 w-full justify-center" data-testid="checkout-submit-button">
            <Send size={16} />
            {submitting
              ? (lang === "fr" ? "Redirection vers Stripe…" : "Redirecting to Stripe…")
              : items.length === 0
                ? t.cart.empty
                : (lang === "fr" ? "Payer avec Stripe" : lang === "de" ? "Mit Stripe bezahlen" : lang === "it" ? "Paga con Stripe" : "Pay with Stripe")}
          </button>

          {fallbackUrl && (
            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/40 space-y-3" data-testid="stripe-fallback-block">
              <p className="font-mono text-[11px] uppercase tracking-widest text-yellow-300 leading-relaxed">
                {lang === "fr"
                  ? "La redirection vers Stripe semble bloquée par votre navigateur (cookies tiers, extension, mode privé). Utilisez le bouton ci-dessous pour ouvrir le paiement dans un nouvel onglet :"
                  : "Stripe redirect appears blocked by your browser. Use the button below to open the payment in a new tab:"}
              </p>
              <a
                href={fallbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-neon w-full justify-center"
                data-testid="stripe-fallback-link"
              >
                {lang === "fr" ? "Ouvrir Stripe dans un nouvel onglet ↗" : "Open Stripe in new tab ↗"}
              </a>
              <p className="font-mono text-[10px] text-zinc-500 text-center">
                {lang === "fr"
                  ? "Astuce : essayez Chrome ou Firefox, désactivez les bloqueurs de pub, ou utilisez la navigation normale (pas privée)."
                  : "Tip: try Chrome or Firefox, disable ad-blockers, or use normal browsing (not private)."}
              </p>
            </div>
          )}
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-center -mt-1">
            🔒 {lang === "fr" ? "Paiement sécurisé · Carte bancaire" : lang === "de" ? "Sichere Zahlung · Karte" : lang === "it" ? "Pagamento sicuro · Carta" : "Secure payment · Card"}
          </p>
        </form>

        <aside className="bg-[#080808] border border-white/5 p-6 h-fit sticky top-28" data-testid="checkout-summary">
          <div className="label-tech mb-4">{t.cart.summary}</div>
          {items.length === 0 ? (
            <p className="text-zinc-500 font-body text-sm">
              {t.cart.empty}{" "}
              <Link to="/produits" className="text-[#39FF14] hover:underline">
                {t.cart.continue}
              </Link>
            </p>
          ) : (
            <div className="divide-y divide-white/5 mb-6">
              {items.map((item) => (
                <OrderItemCard key={item.lineId} item={item} lang={lang} compact />
              ))}
            </div>
          )}
          <p className="text-xs text-zinc-500 font-body leading-relaxed">{t.cart.note}</p>
          {(() => {
            const subtotal = items.reduce((s, it) => s + (Number(it.unit_price_chf) || 0) * (it.quantity || 1), 0);
            if (subtotal <= 0) return null;
            return (
              <div className="mt-5 pt-4 border-t border-white/10" data-testid="checkout-total">
                <VolumeDiscountRow subtotal={subtotal} lang={lang} compact />
              </div>
            );
          })()}
        </aside>
      </section>
    </div>
  );
}
